import React, { useCallback, useMemo, useState } from 'react';
import { useCss } from 'react-use';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToString from 'utils/converters/fromAnyToString';
import asObject from 'utils/objects/asObject.util';
import C from 'utils/values/constant.util';

import ColorPickerContainer from 'onboarding/common/colorPicker/ColorMenu';
import InputFieldContainer from 'onboarding/common/InputField';

import 'styles/onboarding/colorpicker.scss';
import CN from './EditCategory.module.scss';

// should also change in CategoriesScreen.jsx and EditCategoryWrap.jsx
const ITEMS = C([
  { hex: '#3DA49D' },
  { hex: '#304E8C' },
  { hex: '#E55a6c' },
  { hex: '#c93b7e' },
  { hex: '#5E72D1' },
  { hex: '#753A97' },
  { hex: '#B82493' },
  { hex: '#E06491' },
  { hex: '#9F5FC0' },
  { hex: '#7300AF' },
  { hex: '#4E2082' },
  { hex: '#9E034C' },
  { hex: '#3B2A4C' },
  { hex: '#E06900' },
  { hex: '#C54629' },
  { hex: '#426175' },
  { hex: '#007ba0' },
  { hex: '#3A8463' },
  { hex: '#4CAC6A' },
  { hex: '#D29700' },
]);

const EditCategory = ({ category, changeCategoryColor, changeCategoryName, children }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const { color, name } = asObject(category);

  const [activeItem, setActiveItem] = useState({ hex: color || '#000000' });
  const bulletColor = useCss({ backgroundColor: activeItem.hex });

  const { val: isPickerRendered, tip: togglePicker } = useTip(false);
  const { val: isEditable } = useTip(true);

  const handleMakeActive = useCallback(
    (item) => {
      setActiveItem(item);
      changeCategoryColor(item.hex);
    },
    [changeCategoryColor],
  );

  const data = useMemo(
    () => ({
      id: 'CategoryNameInput',
      type: 'text',
      placeholder: '',
      inputValue: fromAnyToString(name),
    }),
    [name],
  );

  return (
    <div
      data-bem="EditCategory"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div className={CN.wrapper}>
        <div className={CN.label}>{t('composite.editCategory.nameLabel')}</div>
        <InputFieldContainer data={data} autoFocus onChangeHandler={changeCategoryName} />
      </div>

      <div className={CN.wrapper}>
        <div className={CN.label}>{t('composite.editCategory.colourLabel')}</div>
        <div
          className={cn({
            [CN.container]: true,
            [CN.isOpen]: isPickerRendered,
          })}
        >
          <div className={CN.flex}>
            {isEditable && (
              <div className={CN.editColor}>
                <div
                  data-bem="EditCategory__dot"
                  className={cn({
                    [CN.dot]: true,
                    [bulletColor]: bulletColor,
                  })}
                />

                <Button variant="secondary" onClick={togglePicker}>
                  <span className={CN.buttonGap}>{t('composite.editCategory.editButton')}</span>
                </Button>
              </div>
            )}
          </div>
          {isPickerRendered && (
            <ColorPickerContainer
              id="colorPicker"
              items={ITEMS}
              activeItem={activeItem}
              makeActive={handleMakeActive}
            />
          )}

          {children}
        </div>
      </div>
      <div className={CN.subTitle}>
        {name} {t('composite.editCategory.services')}
      </div>
    </div>
  );
};

export default EditCategory;
